import { useMutation, useQuery } from '@tanstack/react-query';
import {
  archiveReport,
  deleteReport,
  executeReportAction,
  getReport,
  getReportDatasets,
  getReportExtensions,
  getReportMetrics,
  getReportOpsConfig,
  getReports,
  getReportExportStatus,
  getReportRefreshStatus,
  queryReport,
  exportReport,
  refreshReport,
  unarchiveReport,
  updateReport,
} from './reportsApi';

export const createReportsHooks = ({ queryClient }) => {
  const useReports = (params = {}) =>
    useQuery({
      queryKey: ['reports', params],
      queryFn: () => getReports(params),
      keepPreviousData: true,
    });

  const useReport = (reportId) =>
    useQuery({
      queryKey: ['report', reportId],
      queryFn: () => getReport(reportId),
      enabled: Boolean(reportId),
    });

  const useReportDatasets = () =>
    useQuery({
      queryKey: ['reportDatasets'],
      queryFn: getReportDatasets,
      staleTime: 10 * 60 * 1000,
    });

  const useReportMetrics = () =>
    useQuery({
      queryKey: ['reportMetrics'],
      queryFn: getReportMetrics,
      staleTime: 10 * 60 * 1000,
    });

  const useReportExtensions = () =>
    useQuery({
      queryKey: ['reportExtensions'],
      queryFn: getReportExtensions,
      staleTime: 10 * 60 * 1000,
    });

  const useReportOpsConfig = () =>
    useQuery({
      queryKey: ['reportOpsConfig'],
      queryFn: getReportOpsConfig,
      staleTime: 10 * 60 * 1000,
    });

  const useReportQuery = (reportId, params = {}) =>
    useQuery({
      queryKey: ['reportQuery', reportId, params],
      queryFn: () => queryReport({ reportId, ...params }),
      enabled: Boolean(reportId),
      keepPreviousData: true,
    });

  const useReportExport = () =>
    useMutation({
      mutationFn: exportReport,
    });

  const useReportExportStatus = (exportId, options = {}) =>
    useQuery({
      queryKey: ['reportExportStatus', exportId],
      queryFn: () => getReportExportStatus(exportId),
      enabled: Boolean(exportId),
      refetchInterval: options.pollInterval || false,
    });

  const useReportRefresh = () =>
    useMutation({
      mutationFn: refreshReport,
    });

  const useReportAction = () =>
    useMutation({
      mutationFn: executeReportAction,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['report', variables?.reportId] });
        queryClient.invalidateQueries({ queryKey: ['reportQuery', variables?.reportId] });
      },
    });

  const useReportRefreshStatus = (reportId, options = {}) =>
    useQuery({
      queryKey: ['reportRefreshStatus', reportId],
      queryFn: () => getReportRefreshStatus(reportId),
      enabled: Boolean(reportId),
      refetchInterval: options.pollInterval || false,
    });

  const useUpdateReport = () =>
    useMutation({
      mutationFn: updateReport,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['reports'] });
        queryClient.invalidateQueries({ queryKey: ['report', variables?.reportId] });
      },
    });

  const useArchiveReport = () =>
    useMutation({
      mutationFn: archiveReport,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['reports'] });
      },
    });

  const useUnarchiveReport = () =>
    useMutation({
      mutationFn: unarchiveReport,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['reports'] });
      },
    });

  const useDeleteReport = () =>
    useMutation({
      mutationFn: deleteReport,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['reports'] });
      },
    });

  return {
    useReports,
    useReport,
    useReportDatasets,
    useReportMetrics,
    useReportExtensions,
    useReportOpsConfig,
    useReportQuery,
    useReportExport,
    useReportExportStatus,
    useReportRefresh,
    useReportRefreshStatus,
    useReportAction,
    useUpdateReport,
    useArchiveReport,
    useUnarchiveReport,
    useDeleteReport,
  };
};
